import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./button";

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

function DialogTrigger({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger className={cn(buttonVariants(), className)} {...props} />;
}

function DialogContent({
  className,
  children,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/50" />
      <DialogContentSurface className={className} onCloseAutoFocus={onCloseAutoFocus} {...props}>
        {children}
      </DialogContentSurface>
    </DialogPrimitive.Portal>
  );
}

/**
 * Most dialogs in the console open from a shortcut or a menu item rather than a `DialogTrigger`,
 * and Radix only restores focus to a trigger. This surface remembers whatever had focus when the
 * dialog opened and hands focus back to it on close, including when one dialog opened from
 * inside another.
 */
function DialogContentSurface({
  className,
  children,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  const [previouslyFocused] = React.useState<HTMLElement | null>(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  return (
    <DialogPrimitive.Content
      className={cn(
        // Bounded by the dynamic viewport and scrollable inside, so a tall form keeps its actions
        // reachable instead of pushing them below the fold. dvh rather than vh so mobile browser
        // chrome does not clip the bottom.
        "fixed top-20 left-1/2 z-50 flex max-h-[calc(100dvh-6rem)] w-[min(54rem,calc(100%-2rem))] -translate-x-1/2 flex-col overflow-y-auto rounded-[var(--radius-dialog)] border bg-popover p-6 text-popover-foreground shadow-dialog outline-none",
        className,
      )}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented) return;
        if (!previouslyFocused?.isConnected) return;
        event.preventDefault();
        previouslyFocused.focus();
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className="absolute top-4 right-4 inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:size-8"
        aria-label="Close"
      >
        <X className="size-4" aria-hidden />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-xl font-semibold tracking-tight", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("mt-1 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger };
